<template>
  <div>
    <v-container v-if="!loadingPage" fluid>
      <v-card style="width: 100%" elevation="2">
        <v-card-title class="headline">
          {{ patientFormSign.formSign.name }}
        </v-card-title>
        <v-card-text>
          <v-expand-transition>
            <div v-if="pdfLoaded">
              <div ref="pdfPreview" class="pdf-preview"></div>
            </div>
            <v-progress-circular
              v-else
              indeterminate
              color="primary"
              class="mt-4"
            ></v-progress-circular>
          </v-expand-transition>
        </v-card-text>
      </v-card>

      <form>
        <div
          v-if="forms && forms.length"
          v-for="(section, sectionIndex) in forms"
          :key="sectionIndex"
          class="mb-6"
        >
          <v-card elevation="2">
            <v-card-title class="subtitle-1 font-weight-bold">{{
              section.section
            }}</v-card-title>
            <v-card-text>
              <br />
              <v-row>
                <v-col
                  v-if="section.items && section.items.length"
                  v-for="(form, formIndex) in section.items"
                  :key="formIndex"
                  cols="12"
                >
                  <div v-if="form.type === 'sign'" class="signature-container">
                    <v-label class="mb-2">{{ form.label }}</v-label>
                    <div class="signature-box">
                      <img
                        v-if="form.value"
                        :src="form.value"
                        class="signature-image"
                      />
                      <div v-else class="signature-placeholder">
                        No signature
                      </div>
                    </div>
                    <div class="mt-2 text-center">
                      <v-btn
                        @click="openSignatureDialog(sectionIndex, formIndex)"
                        small
                        color="primary"
                        class="mr-2"
                        >Sign</v-btn
                      >
                      <v-btn
                        @click="triggerFileUpload(sectionIndex, formIndex)"
                        small
                        color="info"
                        class="mr-2"
                      >
                        <v-icon left small>mdi-upload</v-icon> Upload
                      </v-btn>
                      <v-btn
                        @click="clearSignature(sectionIndex, formIndex)"
                        small
                        outlined
                        color="error"
                        >Clear</v-btn
                      >
                    </div>
                  </div>
                  <v-text-field
                    v-else
                    :label="form.label"
                    v-model="form.value"
                    :type="form.type"
                    outlined
                    dense
                  >
                  </v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <v-row>
          <v-col cols="12" sm="6">
            <v-btn
              @click="editPatientFormSign"
              class="mt-7"
              dark
              color="#ae1431"
              elevation="0"
              block
            >
              <v-icon left>mdi-check</v-icon>
              Save edit
            </v-btn>
          </v-col>
          <v-col cols="12" sm="6">
            <v-btn
              class="mt-7"
              outlined
              elevation="0"
              @click="cancelHandler"
              block
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
          </v-col>
        </v-row>
      </form>

      <!-- Signature Dialog -->
      <v-dialog v-model="signatureDialog" max-width="600px">
        <v-card>
          <v-card-title class="headline">Sign Here</v-card-title>
          <v-card-text>
            <div class="signature-pad-container">
              <canvas ref="signatureCanvas"></canvas>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn @click="clearCurrentSignature" text>Clear</v-btn>
            <v-spacer></v-spacer>
            <v-btn @click="closeSignatureDialog(false)" text>Cancel</v-btn>
            <v-btn @click="closeSignatureDialog(true)" color="light-grey"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
    <v-progress-circular
      v-if="loadingPage"
      color="primary"
      indeterminate
      class="loading-indicator"
    ></v-progress-circular>
    <input
      type="file"
      ref="globalSignatureInput"
      style="display: none"
      accept="image/png, image/jpeg, image/jpg"
      @change="handleFileUpload($event)"
    />
  </div>
</template>

<script>
import { PDFDocument } from "pdf-lib";
import SignaturePad from "signature_pad";
import * as pdfjsLib from "pdfjs-dist";
import apolloClient from "@/apollo/apollo";
import gql from "graphql-tag";
import _ from "lodash";

export default {
  data() {
    return {
      loadingPage: false,
      errorMessages: null,
      forms: [],
      patientFormSign: null,
      pdfDoc: null,
      signaturePads: {},
      pdfLoaded: false,
      namePage: 1,
      totalPages: 0,
      signatureDialog: false,
      currentSignaturePad: null,
      currentSectionIndex: null,
      currentFormIndex: null,
    };
  },

  async mounted() {
    try {
      pdfjsLib.GlobalWorkerOptions.workerSrc = await import(
        "pdfjs-dist/build/pdf.worker.mjs"
      );
    } catch (e) {
      console.error("Error setting up PDF.js worker:", e);
    }

    try {
      const response = await apolloClient.query({
        query: gql`
          query findOnePatientFormSignByPatientFormSignId(
            $patientFormSignId: Int!
          ) {
            findOnePatientFormSignByPatientFormSignId(
              patientFormSignId: $patientFormSignId
            ) {
              id
              pdfUrl
              formJsonAnswers {
                name
                value
              }
              formSign {
                id
                name
                pdfUrl
                formJsonQuestion {
                  name
                  type
                  label
                  order
                  section
                }
              }
            }
          }
        `,
        variables: {
          patientFormSignId: +this.$route.params.patientFormSignId,
        },
        context: {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        },
      });

      if (
        response.data &&
        response.data.findOnePatientFormSignByPatientFormSignId
      ) {
        this.patientFormSign =
          response.data.findOnePatientFormSignByPatientFormSignId;

        let answersMap = new Map();
        try {
          answersMap = new Map(
            this.patientFormSign.formJsonAnswers.map((answer) => [
              answer.name,
              answer,
            ])
          );
        } catch (e) {
          console.error("Error creating answersMap:", e);
        }

        const formItems = this.patientFormSign.formSign.formJsonQuestion.map(
          (item) => {
            const answer = answersMap.get(item.name);
            let val = answer ? answer.value : null;
            // Fix signature display if prefix is missing
            if (item.type === "sign" && val && !val.startsWith("data:image")) {
              val = `data:image/png;base64,${val}`;
            }

            return {
              name: item.name,
              value: val,
              type: item.type,
              label: item.label,
              section: item.section,
              order: item.order,
            };
          }
        );

        const grouped = _.groupBy(formItems, "section");
        console.log("Grouped items:", grouped);

        this.forms = Object.keys(grouped).map((key) => ({
          section: "" + key,
          items: grouped[key] || [], // Ensure items is always an array
        }));

        console.log("Processed forms:", JSON.parse(JSON.stringify(this.forms)));

        this.$nextTick(() => {
          this.loadPDF();
        });
      } else {
        throw new Error("No data returned from the query");
      }
    } catch (error) {
      console.error("Failed to fetch form sign:", error);
      this.errorMessages = `Failed to fetch form sign: ${error.message}`;
    }
    this.closeSignatureDialog(true);
  },
  methods: {
    cancelHandler() {
      this.$router.back();
    },
    async editPatientFormSign() {
      try {
        this.loadingPage = true;
        const result = await this.$store.dispatch("editPatientFormSign", {
          forms: this.forms,
          patientFormSignId: this.patientFormSign.id,
        });

        this.loadingPage = false;

        if (result.success && result.pdfBase64) {
          // Open PDF preview in new tab with download option
          this.openPdfPreview(result.pdfBase64);
        } else if (result.success) {
          // If no PDF returned, just go back
          this.$router.back();
        } else {
          throw new Error("Failed to edit patient form sign");
        }
      } catch (error) {
        console.error("Error editing patient form sign:", error);
        this.errorMessages = `Failed to edit patient form sign: ${error.message}`;
        this.loadingPage = false;
      }
    },
    openPdfPreview(pdfBase64) {
      // Convert base64 to blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const filename = `${this.patientFormSign.formSign.name.replace(
        /[\\/:*?"<>|]/g,
        "_"
      )}.pdf`;

      // Open preview window with download button
      const previewWindow = window.open("", "_blank");
      if (previewWindow) {
        const html = `<!doctype html>
<html><head>
  <meta charset="utf-8" />
  <title>${filename}</title>
  <style>
    html, body { height: 100%; margin: 0; background: #f7f7f7; }
    .toolbar { position: fixed; top: 0; left: 0; right: 0; background: #fff; padding: 8px 12px; box-shadow: 0 1px 3px rgba(0,0,0,.1); z-index: 1; display: flex; gap: 8px; align-items: center; }
    .btn { background:#10938F; color:#fff; padding:6px 12px; border-radius:4px; text-decoration:none; font: 14px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; cursor: pointer; border: none; }
    .btn:hover { background:#0d7a78; }
    .btn-close { background:#ae1431; }
    .btn-close:hover { background:#8a1027; }
    .viewer { position: absolute; top: 44px; left: 0; right: 0; bottom: 0; border: 0; width: 100%; height: calc(100% - 44px); }
  </style>
</head>
<body>
  <div class="toolbar">
    <a href="${url}" download="${filename}" class="btn">Download PDF</a>
    <button onclick="window.close()" class="btn btn-close">Close</button>
  </div>
  <iframe class="viewer" src="${url}"></iframe>
  <script>
    window.addEventListener('beforeunload', function(){ try { URL.revokeObjectURL('${url}'); } catch(e){} });
  <\/script>
</body></html>`;

        previewWindow.document.open();
        previewWindow.document.write(html);
        previewWindow.document.close();
      } else {
        // Fallback: download directly if popup blocked
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 2000);
      }
    },
    async loadPDF() {
      try {
        const fullurl = this.patientFormSign.formSign.pdfUrl;
        console.log("Attempting to load PDF from URL:", fullurl);
        const response = await fetch(fullurl);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/pdf")) {
          console.warn(
            "The content type is not PDF. Content-Type:",
            contentType
          );
        }
        const arrayBuffer = await response.arrayBuffer();
        if (arrayBuffer.byteLength === 0) {
          throw new Error("The PDF file is empty");
        }

        console.log("PDF file size:", arrayBuffer.byteLength, "bytes");

        try {
          this.pdfDoc = await PDFDocument.load(arrayBuffer);
        } catch (pdfLibError) {
          console.error("pdf-lib failed to load the PDF:", pdfLibError);

          const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
          const pdf = await loadingTask.promise;
          console.log("PDF loaded successfully with pdf.js");
          this.totalPages = pdf.numPages;
          this.pdfLoaded = true;
          this.$nextTick(() => {
            this.renderPDF(pdf);
          });
          return;
        }

        this.pdfLoaded = true;
        this.totalPages = this.pdfDoc.getPageCount();
        this.$nextTick(() => {
          this.renderPDF();
        });
      } catch (error) {
        console.error("Failed to load PDF:", error);
        this.errorMessages = `Failed to load PDF: ${error.message}`;
      }
    },

    async renderPDF(pdfDoc = null) {
      try {
        let pdf;
        if (pdfDoc) {
          pdf = pdfDoc; // Use the pdf.js document if provided
        } else {
          const fullurl = this.patientFormSign.formSign.pdfUrl;

          const loadingTask = pdfjsLib.getDocument(fullurl);
          pdf = await loadingTask.promise;
        }
        this.totalPages = pdf.numPages;

        const pdfPreview = this.$refs.pdfPreview;
        pdfPreview.innerHTML = "";

        const pixelRatio = window.devicePixelRatio || 1;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const baseViewport = page.getViewport({ scale: 1.0 });

          // Calculate the CSS scale to fit the container width
          const containerWidth = pdfPreview.clientWidth || window.innerWidth - 48;
          const fitScale = containerWidth / baseViewport.width;

          // Render at high resolution: use at least 3.0 or pixelRatio × fitScale
          const renderScale = Math.max(3.0, fitScale * pixelRatio);
          const viewport = page.getViewport({ scale: renderScale });

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          // Set canvas internal resolution to the high-res render size
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);

          // Set CSS display size to the logical (fit-to-container) size
          canvas.style.width = Math.floor(baseViewport.width * fitScale) + "px";
          canvas.style.height = Math.floor(baseViewport.height * fitScale) + "px";

          await page.render({ canvasContext: context, viewport: viewport });

          const pageContainer = document.createElement("div");
          pageContainer.className = "pdf-page";
          pageContainer.appendChild(canvas);

          const pageNumber = document.createElement("div");
          pageNumber.className = "page-number";
          pageNumber.textContent = `Page ${pageNum}`;
          pageContainer.appendChild(pageNumber);
          pdfPreview.appendChild(pageContainer);
        }
      } catch (error) {
        console.error("Failed to render PDF:", error);
        this.errorMessages = `Failed to render PDF: ${error.message}`;
      }
    },
    openSignatureDialog(sectionIndex, formIndex) {
      this.currentSectionIndex = sectionIndex;
      this.currentFormIndex = formIndex;
      this.signatureDialog = true;
      this.$nextTick(() => {
        this.initializeSignaturePad();
      });
    },
    async initializeSignaturePad() {
      await this.$nextTick();
      const canvas = this.$refs.signatureCanvas;
      if (!canvas) {
        console.error("Signature canvas not found");
        return;
      }

      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      if (this.signaturePad) {
        this.signaturePad.off();
      }

      this.signaturePad = new SignaturePad(canvas, {
        minWidth: 1,
        maxWidth: 2,
        penColor: "rgb(0, 0, 0)",
      });

      // Load existing signature if available
      await this.$nextTick();
      const existingSignature =
        this.forms[this.currentSectionIndex].items[this.currentFormIndex].value;
      if (existingSignature) {
        try {
          await this.loadSignatureImage(this.signaturePad, existingSignature);
        } catch (error) {
          console.error("Failed to load existing signature:", error);
        }
      }
    },

    closeSignatureDialog(save) {
      if (save && this.signaturePad && !this.signaturePad.isEmpty()) {
        const signatureData = this.signaturePad.toDataURL();
        // Use $set for reactivity
        const item =
          this.forms[this.currentSectionIndex].items[this.currentFormIndex];
        this.$set(item, "value", signatureData);
      }
      this.signatureDialog = false;
      if (this.signaturePad) {
        this.signaturePad.off();
        this.signaturePad = null;
      }
      this.currentSectionIndex = null;
      this.currentFormIndex = null;
    },

    clearCurrentSignature() {
      if (this.signaturePad) {
        this.signaturePad.clear();
      }
    },

    clearSignature(sectionIndex, formIndex) {
      this.forms[sectionIndex].items[formIndex].value = null;
    },

    loadSignatureImage(signaturePad, dataUrl) {
      return new Promise((resolve, reject) => {
        if (!signaturePad || !signaturePad._canvas) {
          console.error("SignaturePad or canvas is not available");
          reject("Canvas not available");
          return;
        }

        const canvas = signaturePad._canvas;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          console.error("Unable to get 2D context from canvas");
          reject("Context not available");
          return;
        }

        const image = new Image();
        image.onload = () => {
          signaturePad.clear(); // Clear existing signature
          // Draw image centered and scaled to fit if necessary
          const hRatio = canvas.width / image.width;
          const vRatio = canvas.height / image.height;
          const ratio = Math.min(hRatio, vRatio);
          const centerShift_x = (canvas.width - image.width * ratio) / 2;
          const centerShift_y = (canvas.height - image.height * ratio) / 2;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(
            image,
            0,
            0,
            image.width,
            image.height,
            centerShift_x,
            centerShift_y,
            image.width * ratio,
            image.height * ratio
          );

          signaturePad._isEmpty = false;
          resolve();
        };
        image.onerror = () => {
          console.error("Failed to load signature image");
          reject("Image load failed");
        };
        image.src = dataUrl;
      });
    },
    triggerFileUpload(sectionIndex, formIndex) {
      this.currentSectionIndex = sectionIndex;
      this.currentFormIndex = formIndex;
      // Trigger the hidden file input
      // Since there is a v-for, we have one input or many?
      // The template above put the input inside the loop. So references will be an array.
      // But wait, having multiple inputs with same ref?
      // Better to have one global input or access via array.
      // The previous replace put the input inside the v-for loop.
      // So this.$refs.signatureFileInput will be an array of elements.
      // We need to find the correct one. Since the input is inside the loop where form.type === 'sign',
      // we need to know the index in the filtered list of sign inputs, or just assume the structure.
      // Actually, relying on index in `this.$refs.signatureFileInput` might be tricky because it depends on render order.,
      // A better approach is to have ONE file input outside the loop and trigger it.
      // But I already put it in the loop. Let's fix that in next step or try to use it.
      // If it's in the loop, I can use the event target approach if I clicked the label, but here I click a button.
      // Let's assume I move the input to be global in a subsequent edit or try to access it.
      // If I use one global input, it is safer.
      // I will implement a global input trigger.

      // Accessing the specific input:
      // Since it's in a v-for, refs become arrays.
      // We can try to click the specific one if we can identify it.
      // It is easier to just have one single invisible input at the root of the component.

      this.$refs.globalSignatureInput.click();
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        // Update the form value directly with reactivity
        if (
          this.currentSectionIndex !== null &&
          this.currentFormIndex !== null
        ) {
          const section = this.forms[this.currentSectionIndex];
          const item = section.items[this.currentFormIndex];
          // Use $set to ensure reactivity, although direct property mod triggers it usually,
          // deep nesting in v-for sometimes has caveats.
          // Also updating the specific item object reference might be needed if the item itself was replaced.
          // But here we just update .value.
          // To be 100% sure:
          this.$set(item, "value", dataUrl);

          // Also force update forms array to trigger re-render of loop if needed
          // this.$set(this.forms, this.currentSectionIndex, section); // not really needed if object property works
        }
        // Reset input so same file can be selected again if needed
        event.target.value = "";
      };
      reader.readAsDataURL(file);
    },
  },
  watch: {
    signatureDialog(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initializeSignaturePad();
        });
      }
    },
  },
};
</script>

<style scoped>
.pdf-preview {
  max-width: 100%;
  overflow-x: auto;
}

.pdf-page {
  margin-bottom: 20px;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.pdf-page canvas {
  display: block;
  max-width: 100%;
  height: auto;
}

.page-number {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 12px;
}

.signature-container {
  display: flex;
  flex-direction: column;
}

.signature-box {
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px;
  width: 100%;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
}

.signature-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.signature-placeholder {
  color: #9e9e9e;
  font-style: italic;
}

.signature-pad-container {
  width: 100%;
  height: 300px;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
}

.signature-pad-container canvas {
  width: 100%;
  height: 100%;
}

.v-card__title {
  background-color: #a8e3e1;
}
</style>
